import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Plus, Trash2 } from "lucide-react";

import { ListaDeReferencias } from "@/components/referencias/ListaDeReferencias";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useDescartarReferencia,
  usePapeleraDeReferencias,
  useReferenciasDelInforme,
  useRestaurarReferencia,
  useValidarReferencia,
  useVaciarPapelera,
} from "@/hooks/useReferencias";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { media, numero } from "@/lib/formatoDeMercado";
import type { ReferenciaDelInforme } from "@/lib/referencias";

/**
 * Lo que este informe ya se llevó del mercado, y qué se decidió sobre cada
 * cosa.
 *
 * PANTALLA APARTE DEL CATÁLOGO, y no una tercera pestaña de aquel. Las dos
 * pestañas del paso 2 son el mismo conjunto partido por clase de evidencia y
 * comparten sus filtros; esto es otro conjunto —el del informe—, con otras
 * acciones y sin filtros. Metido ahí, la barra de filtros quedaría muerta en
 * una de cada tres pestañas.
 *
 * LA PAPELERA VIVE ACÁ ADENTRO, como segunda pestaña, y no en su propia ruta.
 * Es una vista que está vacía casi siempre y que solo tiene sentido al lado de
 * la pregunta "qué tiene este informe": una pantalla propia sería una puerta
 * que casi nunca vale la pena abrir.
 */
export function ReferenciasDelInforme() {
  const { informeId } = useParams();
  const id = Number(informeId);
  const habilitado = Number.isFinite(id) && id > 0 ? id : null;

  const [parametros, setParametros] = useSearchParams();
  const vista = parametros.get("vista") === "papelera" ? "papelera" : "informe";

  const [confirmandoVaciar, setConfirmandoVaciar] = useState(false);

  const enlazadas = useReferenciasDelInforme(habilitado);
  const papelera = usePapeleraDeReferencias(habilitado);

  const validar = useValidarReferencia(habilitado);
  const descartar = useDescartarReferencia(habilitado);
  const restaurar = useRestaurarReferencia(habilitado);
  const vaciar = useVaciarPapelera(habilitado);

  const consulta = vista === "papelera" ? papelera : enlazadas;
  const estado = estadoDeConsulta(consulta);

  const referencias: ReferenciaDelInforme[] =
    vista === "papelera" ? (papelera.data?.referencias ?? []) : (enlazadas.data?.referencias ?? []);

  const ocupada =
    validar.isPending || descartar.isPending || restaurar.isPending || vaciar.isPending;

  const cambiarVista = (valor: string) => {
    const siguientes = new URLSearchParams(parametros);
    if (valor === "informe") siguientes.delete("vista");
    else siguientes.set("vista", valor);
    setParametros(siguientes, { replace: true });
  };

  return (
    /* `p-6 lg:p-8`: el mismo padding horizontal que `TopBar`, para que el
       cuerpo arranque en la vertical del título. Ver el paso 1. */
    <div className="flex flex-col gap-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-ink-muted text-caption">
          Revise cada referencia y marque las que da por buenas. Descartar la saca de este
          informe y no del catálogo: sigue disponible para cualquier otra tasación.
        </p>

        <Button asChild>
          <Link to={`/referencias/${id}`}>
            <Plus className="size-4" />
            Agregar más referencias
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs variant="underline" value={vista} onValueChange={cambiarVista}>
          <TabsList>
            <TabsTrigger value="informe">
              En el informe
              {enlazadas.data && (
                <Badge variant="secondary" className="ml-2 font-mono">
                  {enlazadas.data.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="papelera">
              Papelera
              {papelera.data && papelera.data.total > 0 && (
                <Badge variant="secondary" className="ml-2 font-mono">
                  {papelera.data.total}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {vista === "informe" && enlazadas.data && enlazadas.data.total > 0 && (
          <Resumen datos={enlazadas.data} />
        )}

        {vista === "papelera" && papelera.data && papelera.data.total > 0 && (
          <Button
            variant="destructive"
            onClick={() => setConfirmandoVaciar(true)}
            disabled={ocupada}
          >
            <Trash2 className="size-4" />
            Vaciar papelera
          </Button>
        )}
      </div>

      {/* La misma lista —y el mismo repertorio de estados— que dibujan las dos
          pestañas de mercado de la ficha del informe. */}
      <ListaDeReferencias
        estado={estado}
        referencias={referencias}
        que="las referencias del informe"
        tituloDeError="No se pudieron cargar las referencias"
        error={consulta.error}
        onReintentar={consulta.refetch}
        vacio={
          vista === "papelera"
            ? {
                titulo: "La papelera está vacía",
                descripcion: "Lo que descarte aparece acá y se puede restaurar.",
              }
            : {
                titulo: "Este informe todavía no tiene referencias",
                descripcion: "Compare el predio contra el mercado y agregue las que sirvan.",
              }
        }
        ocupada={ocupada}
        onValidar={(referencia, validada) => validar.mutate({ id: referencia.id, validada })}
        onDescartar={(referencia) => descartar.mutate(referencia.id)}
        onRestaurar={(referencia) => restaurar.mutate(referencia.id)}
      />

      {/* LA ÚNICA CONFIRMACIÓN DEL MÓDULO, porque es el único acto que no se
          deshace. Descartar no la lleva: va a la papelera, que está a un clic
          de distancia y con su contador a la vista.

          El texto dice la consecuencia que NO se ve desde acá: las fichas
          borradas vuelven a ofrecerse en el catálogo, porque lo que las
          mantenía fuera era justamente la fila que se está por borrar. */}
      <AlertDialog open={confirmandoVaciar} onOpenChange={setConfirmandoVaciar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Vaciar la papelera de este informe?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se borran definitivamente {papelera.data?.total ?? 0}{" "}
              {papelera.data?.total === 1 ? "referencia descartada" : "referencias descartadas"}.
              Esto no se puede deshacer, y esas fichas vuelven a ofrecerse en el catálogo de
              mercado: el sistema deja de tener cómo saber que usted ya las revisó y las
              rechazó.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                vaciar.mutate();
                setConfirmandoVaciar(false);
              }}
            >
              Vaciar papelera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * El avance de la revisión y el valor que sale del conjunto.
 *
 * La media es de las VALIDADAS, no de todas, y esa es la diferencia con el
 * panel del catálogo: allá se está tanteando un conjunto y acá se está
 * cerrando uno. Una cifra que incluyera fichas que nadie revisó diría que el
 * trabajo está más avanzado de lo que está.
 *
 * ES LA MISMA CIFRA QUE EL PANEL DEL PASO 2 Y POR ESO SE CALCULA IGUAL. Las dos
 * resumen lo que el tasador eligió a mano, y son dos pantallas de un solo
 * flujo: con "media" acá y "mediana" allá, el mismo número cambiaría de nombre
 * y de cálculo al pasar de una a la otra.
 */
function Resumen({
  datos,
}: {
  datos: { referencias: ReferenciaDelInforme[]; total: number; validadas: number };
}) {
  const validadas = datos.referencias.filter((r) => r.validada);
  const mediaUfHa = media(
    validadas.map((r) => {
      const uf = Number(r.precio_uf);
      const m2 = Number(r.superficie_terreno_m2);

      return Number.isFinite(uf) && Number.isFinite(m2) && m2 > 0 ? uf / (m2 / 10_000) : null;
    }),
  );

  return (
    <div className="flex items-end gap-6">
      <div>
        <p className="text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
          Revisadas
        </p>
        <p className="text-ink font-mono text-sm tabular-nums">
          {datos.validadas} de {datos.total}
        </p>
      </div>

      <div>
        <p className="text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
          UF/ha media
        </p>
        <p className="text-ink font-mono text-sm tabular-nums">
          {mediaUfHa === null ? "—" : numero(mediaUfHa, 0)}
        </p>
      </div>
    </div>
  );
}
